'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';

import { useTranslation } from '@/lib/i18n/i18n.client';
import iconYoutubePlayButtonSrc from '~/svg/IconYoutubePlayButton.svg';
import { getYouTubeVideoId, loadYouTubeIframeAPI } from './utils';
import { SvgIcon } from '@/components/SvgIcon';

export type VideoProps = {
  classNames?: {
    container?: string;
    video?: string;
  };
  url: string;
};

const borderStyles = 'rounded border border-gray-200 dark:border-gray-300';

export function Video(props: VideoProps) {
  if (props.url.includes('youtube.com')) {
    return <VideoYoutube {...props} />;
  }

  return <VideoIframe {...props} />;
}

export function VideoIframe({ url, classNames }: VideoProps) {
  return (
    <div
      className={cn(
        'relative m-0 flex w-full overflow-hidden',
        classNames?.container
      )}
    >
      <iframe
        src={url}
        loading='lazy'
        allowFullScreen
        width='100%'
        className={cn('aspect-video', borderStyles, classNames?.video)}
      />
    </div>
  );
}

function sendVideoEvent(eventName: string, videoData: Record<string, any>) {
  const dataLayer = (window as any).dataLayer || [];
  dataLayer.push({
    event: eventName,
    ...videoData,
  });
}

export function VideoYoutube({ url, classNames }: VideoProps) {
  const { texts } = useTranslation();

  const [showVideo, setShowVideo] = React.useState(false);
  const playerRef = React.useRef<any>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const videoStartedRef = React.useRef(false);
  const progressTrackedRef = React.useRef<{ [key: number]: boolean }>({});

  function handleClick() {
    setShowVideo(true);
  }

  const embedID = getYouTubeVideoId(url);

  React.useEffect(() => {
    if (!showVideo || !embedID) return;
    const w: any = window as any;
    let cancelled = false;
    loadYouTubeIframeAPI().then(() => {
      if (cancelled) return;
      if (playerRef.current || !containerRef.current) return;

      playerRef.current = new w.YT.Player(containerRef.current, {
        videoId: embedID,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1,
          rel: 0,
          showinfo: 0,
          modestbranding: 1,
          enablejsapi: 1,
          origin: window.location.origin,
          playsinline: 1,
        },
        events: {
          onReady: handlePlayerReady,
          onStateChange: handlePlayerStateChange,
          onError: handlePlayerError,
        },
      });
    });
    return () => {
      cancelled = true;
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [showVideo, embedID]);

  function handlePlayerReady(event: any) {
    sendVideoEvent('video_ready', {
      video_id: embedID,
      video_title:
        event.target.getVideoData().title || texts.video.youtube_video,
      video_url: url,
    });
  }

  function handlePlayerStateChange(event: any) {
    const state = event.data;
    const w: any = window;

    const videoData = {
      video_id: embedID,
      video_title:
        event.target.getVideoData().title || texts.video.youtube_video,
      video_url: url,
      video_duration: event.target.getDuration(),
      video_current_time: event.target.getCurrentTime(),
    };

    if (state === w.YT.PlayerState.PLAYING) {
      if (!videoStartedRef.current) {
        videoStartedRef.current = true;
        sendVideoEvent('video_start', videoData);
      }
      sendVideoEvent('video_play', videoData);
    } else if (state === w.YT.PlayerState.PAUSED) {
      sendVideoEvent('video_pause', {
        ...videoData,
        percent_complete:
          (event.target.getCurrentTime() / event.target.getDuration()) * 100,
      });
    } else if (state === w.YT.PlayerState.ENDED) {
      sendVideoEvent('video_complete', videoData);
      videoStartedRef.current = false;
      progressTrackedRef.current = {};
    }
  }

  function handlePlayerError(event: any) {
    const errorCode = event.data;
    const errorMessages: { [key: number]: string } = {
      2: 'Invalid parameter',
      5: 'HTML5 player error',
      100: 'Video not found',
      101: 'Video not allowed to be played embedded',
      150: 'Same as 101',
    };

    sendVideoEvent('video_error', {
      video_id: embedID,
      video_url: url,
      error_code: errorCode,
      error_message: errorMessages[errorCode] || texts.video.unknown_error,
    });
  }

  return (
    <div
      className='flex w-full items-center justify-center rounded'
      data-tracker-component='video-popup'
    >
      <div
        onClick={handleClick}
        className={cn(
          'relative flex aspect-video h-full w-full cursor-pointer items-center justify-center bg-cover bg-center',
          borderStyles,
          classNames?.container
        )}
        tabIndex={1}
        style={{
          backgroundImage: `url("https://i.ytimg.com/vi_webp/${embedID}/sddefault.webp")`,
        }}
      >
        {!showVideo && (
          <SvgIcon src={iconYoutubePlayButtonSrc} asImg className='absolute z-10 h-20 w-20' />
        )}
        <div
          ref={containerRef}
          className={cn('aspect-video h-full w-full', classNames?.video)}
        />
      </div>
    </div>
  );
}
